import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import stripePromise from '../services/stripe';
import { t, locale } from '../i18n';

/*
 * Paying by card in the Travel Pass app (server: backend/travel/p3Billing.js): Credit packs, eSIM plans and the
 * Travel Number rental. The card form is Stripe Elements, so card numbers never reach Freshroad's servers.
 */

export const payReady = Boolean(stripePromise);
export const cents = (n) => new Intl.NumberFormat(locale(), { style: 'currency', currency: 'USD' }).format(n / 100);
const CARD_STYLE = { style: { base: { fontSize: '16px', color: '#1a1a1a', '::placeholder': { color: '#9ca3af' } }, invalid: { color: '#c0392b' } } };

/** Wraps the app once so every checkout can use the card form. */
export function PayProvider({ children }) {
  return <Elements stripe={stripePromise}>{children}</Elements>;
}

/** Prices, Credit costs, saved cards and the number rental (GET /api/p3/billing). */
export function useBilling(requestJson, api) {
  const [billing, setBilling] = useState(null);
  const [error, setError] = useState('');
  const reload = useCallback(() => requestJson(api('billing')).then((b) => { setBilling(b); setError(''); return b; }), [requestJson, api]);
  useEffect(() => {
    if (payReady) reload().catch((e) => setError(e.message));
  }, [reload]);
  return { billing, reload, error };
}

/**
 * Pays with the chosen card ('new' saves the typed card first), then finishes a payment the bank wants approved
 * (3-D Secure) and calls the endpoint's confirm route. Returns the server's final answer.
 */
export function usePay(requestJson, api) {
  const stripe = useStripe();
  const elements = useElements();
  return useCallback(async (path, body, card) => {
    let paymentMethodId = card;
    if (card === 'new') {
      const { clientSecret } = await requestJson(api('billing/setup-intent'), { method: 'POST', body: {} });
      const r = await stripe.confirmCardSetup(clientSecret, { payment_method: { card: elements.getElement(CardElement) } });
      if (r.error) throw new Error(r.error.message);
      paymentMethodId = r.setupIntent.payment_method;
      await requestJson(api('billing/payment-methods'), { method: 'POST', body: { paymentMethodId } });
    }
    const r = await requestJson(api(path), { method: 'POST', body: { ...body, ...(paymentMethodId ? { paymentMethodId } : {}) } });
    if (!r.requiresAction) return { ...r, paymentMethodId };
    const approved = await stripe.confirmCardPayment(r.clientSecret);
    if (approved.error) {
      if (path === 'numbers/travel') await requestJson(api('numbers/travel'), { method: 'DELETE' }).catch(() => {});
      throw new Error(approved.error.message);
    }
    return { ...(await requestJson(api(r.confirm.path), { method: 'POST', body: r.confirm.body })), paymentMethodId };
  }, [stripe, elements, requestJson, api]);
}

/** Saved cards plus "New card" (with the Stripe card form), and Make default / Remove for the selected one. */
export function CardPicker({ billing, card, setCard, onChanged, requestJson, api }) {
  const [error, setError] = useState('');
  const cards = useMemo(() => billing?.cards || [], [billing]);
  useEffect(() => {
    if (!card || (card !== 'new' && !cards.some((c) => c.id === card))) setCard(cards.find((c) => c.isDefault)?.id || 'new');
  }, [card, cards, setCard]);
  const act = async (path, method) => {
    setError('');
    try {
      await requestJson(api(path), { method });
      await onChanged();
    } catch (e) {
      setError(e.message);
    }
  };
  const saved = cards.find((c) => c.id === card);
  return (
    <>
      <div className="tb-sub" style={{ margin: 0 }}>{t('shop.payWith')}</div>
      <div className="tb-choices">
        {cards.map((c) => (
          <button key={c.id} type="button" className="tb-choice tb-pay" aria-pressed={card === c.id} onClick={() => setCard(c.id)}>
            <strong dir="ltr">{c.brand.toUpperCase()} •••• {c.last4}</strong>
            {c.isDefault && <small>{t('pa.pay.default')}</small>}
          </button>
        ))}
        <button type="button" className="tb-choice tb-pay" aria-pressed={card === 'new'} onClick={() => setCard('new')}><strong>{t('pa.pay.newCard')}</strong></button>
      </div>
      {card === 'new' && <div className="tb-input" style={{ paddingTop: 12 }} aria-label={t('pa.pay.cardDetails')}><CardElement options={CARD_STYLE} /></div>}
      {saved && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!saved.isDefault && <button type="button" className="tb-ghost" onClick={() => act(`billing/payment-methods/${saved.id}/default`, 'PUT')}>{t('pa.pay.makeDefault')}</button>}
          <button type="button" className="tb-ghost" onClick={() => act(`billing/payment-methods/${saved.id}`, 'DELETE')}>{t('pa.pay.remove')}</button>
        </div>
      )}
      {error && <div className="tb-err" role="alert" style={{ margin: 0 }}>{error}</div>}
      <p className="tb-note" style={{ margin: 0 }}>🔒 {t('pa.pay.secure')}</p>
    </>
  );
}
